import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ScanSearch, X, GripHorizontal } from "lucide-react";
import { useCompare } from "@/contexts/CompareContext";
import { Button } from "@/components/ui/button";

export default function CompareDrawer() {
  const { ids, remove, clear } = useCompare();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest("button") || e.target.closest("a")) return;
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    e.preventDefault();
  };

  const handleTouchStart = (e) => {
    if (e.target.closest("button") || e.target.closest("a")) return;
    const touch = e.touches[0];
    setIsDragging(true);
    dragStart.current = {
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    };
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      setPosition({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y,
      });
    };

    const handleTouchMove = (e) => {
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - dragStart.current.x,
        y: touch.clientY - dragStart.current.y,
      });
    };

    const handleMouseUp = () => setIsDragging(false);

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, [isDragging]);

  if (ids.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 z-50 bg-primary text-primary-foreground rounded-full shadow-2xl px-4 py-2.5 flex items-center gap-3 select-none transition-shadow active:shadow-inner"
      style={{
        transform: `translate(calc(-50% + ${position.x}px), ${position.y}px)`,
        cursor: isDragging ? "grabbing" : "grab",
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      data-testid="compare-drawer"
    >
      <div className="text-primary-foreground/40 hover:text-primary-foreground/80 transition-colors">
        <GripHorizontal className="w-4 h-4" />
      </div>
      <ScanSearch className="w-4 h-4 shrink-0" />
      <div className="text-xs font-semibold">
        <span className="tabular-nums mr-0.5">{ids.length}</span>
        <span className="text-primary-foreground/80">selected</span>
      </div>
      <Button asChild size="sm" variant="secondary" className="font-bold rounded-full h-8 text-[11px] px-3.5" data-testid="compare-go-btn"><Link to="/compare">Compare</Link></Button>
      <button
        type="button"
        onClick={clear}
        className="text-primary-foreground/60 hover:text-primary-foreground p-1 hover:bg-white/10 rounded-full transition-colors"
        aria-label="Clear"
        data-testid="compare-clear-btn"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
